import { fetchWithTimeout, retry } from "@layerswap/utils";
import { parseUnits } from "viem";
/** Subtract before truncating dust, without rounding an unavailable base unit up. */
const getWithdrawableAmount = (total, hold, decimals) => {
    if (![total, hold].every(value => typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value))) {
        throw new Error('Unable to read your Hyperliquid balance. Please try again.');
    }
    const precision = Math.max(decimals, total.split('.')[1]?.length ?? 0, hold.split('.')[1]?.length ?? 0);
    const available = parseUnits(total, precision) - parseUnits(hold, precision);
    return available > 0n ? available / (10n ** BigInt(precision - decimals)) : 0n;
};
export class HyperliquidClient {
    async getClearinghouseState(user, nodeUrl, timeoutMs, retryCount) {
        const response = await retry(async () => await fetchWithTimeout(`${nodeUrl}/info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'clearinghouseState',
                user: user,
            }),
            timeoutMs: timeoutMs ?? 60000,
        }), retryCount ?? 3, 500);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
    async getSpotClearinghouseState(user, nodeUrl, timeoutMs, retryCount) {
        const response = await retry(async () => await fetchWithTimeout(`${nodeUrl}/info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'spotClearinghouseState',
                user: user,
            }),
            timeoutMs: timeoutMs ?? 60000,
        }), retryCount ?? 3, 500);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
    /**
     * Free, withdrawable USDC split across both HyperCore pools. The `sendToEvmWithData`
     * withdrawal can pull from either (spot or perp) via `sourceDex`, so the caller needs
     * the split to pick a pool — or consolidate via `usdClassTransfer` when neither alone
     * covers the amount. Fetches both clearinghouse states concurrently; either failing
     * rejects the whole split (we can't decide a source on partial data).
     */
    async getWithdrawableSplit(user, nodeUrl, coin, decimals, timeoutMs, retryCount) {
        const [spotState, perpsState] = await Promise.all([
            this.getSpotClearinghouseState(user, nodeUrl, timeoutMs, retryCount),
            this.getClearinghouseState(user, nodeUrl, timeoutMs, retryCount),
        ]);
        const balance = spotState.balances.find(b => b.coin.toUpperCase() === coin.toUpperCase());
        const spot = balance ? getWithdrawableAmount(balance.total, balance.hold, decimals) : 0n;
        // `withdrawable` is the free USDC after margin — the correct field, not
        // accountValue/totalRawUsd (which include margin-locked equity).
        const perps = getWithdrawableAmount(perpsState.withdrawable, '0', decimals);
        return { spot, perps, combined: spot + perps };
    }
    /**
     * Submit a signed `sendToEvmWithData` action to Hyperliquid's exchange endpoint.
     * Deliberately NOT wrapped in `retry`: the action carries a time-bound nonce
     * and is signed, so a blind retry risks a double-submit.
     */
    async withdraw(action, signature, nodeUrl, timeoutMs) {
        const response = await fetchWithTimeout(`${nodeUrl}/exchange`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action, nonce: action.nonce, signature }),
            timeoutMs: timeoutMs ?? 60000,
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
    /**
     * Submit a signed `usdClassTransfer` action to move USDC between the user's
     * perp and spot pools. Like `withdraw`, deliberately NOT wrapped in `retry`:
     * the action carries a time-bound nonce and is signed, so a blind retry risks
     * a double-submit.
     */
    async usdClassTransfer(action, signature, nodeUrl, timeoutMs) {
        const response = await fetchWithTimeout(`${nodeUrl}/exchange`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ action, nonce: action.nonce, signature }),
            timeoutMs: timeoutMs ?? 60000,
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
    }
}
